import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Alert, Spinner } from "react-bootstrap";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { getHost, defaultClientId } from "../utils/utils";
import TestPingButton from "./TestPingButton";

const API_MONITOR_WEBHOOKS = (monitorId) =>
  `${getHost()}/api/monitors/${monitorId}/webhooks`;
const getToken = () => localStorage.getItem("jwt");

const EditMonitorModal = ({ show, onHide, monitor, clients }) => {
  const [selectedClient, setSelectedClient] = useState("");
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Check if monitor has regions support
  const hasRegions = monitor?.has_regions || false;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedClient(defaultClientId(clients));
      setWebhooks([]);
      setMessage("");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // When a client is selected, fetch the webhooks for this monitor/client
  useEffect(() => {
    if (!monitor || !selectedClient) return;

    setLoading(true);
    setWebhooks([]);

    fetch(`${API_MONITOR_WEBHOOKS(monitor._id)}?client_id=${selectedClient}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setWebhooks(data.webhooks || []);
        setLoading(false);
      })
      .catch(() => {
        setWebhooks([]);
        setLoading(false);
      });
  }, [monitor, selectedClient]);

  const addWebhook = () => {
    setWebhooks([...webhooks, { webhook_url: "", regions: [], enabled: true }]);
  };

  const removeWebhook = (index) => {
    setWebhooks(webhooks.filter((_, i) => i !== index));
  };

  const updateWebhook = (index, field, value) => {
    const updated = [...webhooks];
    updated[index][field] = value;
    setWebhooks(updated);
  };

  const handleSave = () => {
    setSaving(true);
    setMessage("");

    const validWebhooks = webhooks
      .filter((w) => w.webhook_url && w.webhook_url.trim() !== "")
      .map((w) => ({
        webhook_url: w.webhook_url,
        regions:
          typeof w.regions === "string"
            ? w.regions
                .split(",")
                .map((r) => r.trim().toLowerCase())
                .filter((r) => r)
            : Array.isArray(w.regions)
              ? w.regions
              : [],
        enabled: w.enabled !== false,
      }));

    fetch(API_MONITOR_WEBHOOKS(monitor._id), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        client_id: selectedClient,
        webhooks: validWebhooks,
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error();
        const data = await res.json();
        setWebhooks(data.webhooks || []);
        setMessage("Webhooks updated!");
        setSaving(false);
      })
      .catch(() => {
        setMessage("Failed to update webhooks.");
        setSaving(false);
      });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title className="text-main d-flex align-items-start gap-3">
          <FiEdit className="fs-24 flex-shrink-0" />
          <div>
            <div className="fs-18 fw-600 lh-1">Editing {monitor?.name}</div>
            <div className="fs-14 text-white-45 mt-1">
              Set your dedicated webhooks
              {hasRegions && (
                <span className="ms-2 badge bg-success">Regions Enabled</span>
              )}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-white modal-white-bg">
        {message && (
          <Alert
            variant={message === "Webhooks updated!" ? "success" : "danger"}
          >
            {message}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="text-main">Select Group/Client</Form.Label>
          <Form.Select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="form-input-field rounded-3 text-main custom-select-white fs-16"
          >
            <option value="">Select a group...</option>
            {clients.map((cl) => (
              <option key={cl._id} value={cl._id}>
                {cl.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {selectedClient && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Label className="text-main mb-0">Webhook URLs</Form.Label>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addWebhook}
                disabled={loading}
              >
                <FiPlus />
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" variant="light" />
                <p className="text-white-65 mt-2 mb-0">Loading webhooks...</p>
              </div>
            ) : webhooks.length === 0 ? (
              <div className="text-center py-3 text-white-65">
                No webhooks configured. Click + to add one.
              </div>
            ) : (
              webhooks.map((webhook, index) => {
                const enabled = webhook.enabled !== false;
                return (
                <div
                  key={index}
                  className="border rounded p-3 mb-3"
                  style={enabled ? undefined : { opacity: 0.5 }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-main">#{index + 1}</small>
                      <Form.Check
                        type="switch"
                        id={`webhook-enabled-${index}`}
                        checked={enabled}
                        onChange={(e) =>
                          updateWebhook(index, "enabled", e.target.checked)
                        }
                        label={
                          <small className="text-white-65">
                            {enabled ? "Active" : "Paused"}
                          </small>
                        }
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <TestPingButton url={webhook.webhook_url} clientId={selectedClient} />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeWebhook(index)}
                      >
                        <FiTrash2 />
                      </Button>
                    </div>
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Control
                      type="text"
                      value={webhook.webhook_url}
                      onChange={(e) =>
                        updateWebhook(index, "webhook_url", e.target.value)
                      }
                      placeholder="Enter webhook URL"
                      className="mb-2"
                    />
                  </Form.Group>
                  {/* Only show regions input if monitor supports it */}
                  {hasRegions && (
                    <Form.Group>
                      <Form.Control
                        type="text"
                        value={
                          Array.isArray(webhook.regions)
                            ? webhook.regions.join(", ")
                            : webhook.regions || ""
                        }
                        onChange={(e) =>
                          updateWebhook(index, "regions", e.target.value)
                        }
                        placeholder="Regions (comma-separated, e.g., us, eu, uk)"
                        size="sm"
                      />
                    </Form.Group>
                  )}
                </div>
                );
              })
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="btn-secondary rounded-3 py-1 px-3 fw-600 fs-12"
          onClick={onHide}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="btn-primary rounded-3 py-1 px-3 fw-600 fs-12"
          onClick={handleSave}
          disabled={saving || !selectedClient || loading}
        >
          {saving ? <Spinner size="sm" /> : "Update"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditMonitorModal;
